import { AsyncPipe, DatePipe, NgClass, NgStyle, UpperCasePipe } from "@angular/common";
import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { formatDisplayCurrencyAmount } from "@core/currency-display.util";
import { ActiveStatus } from "@interfaces/subscription";
import { IonicModule, ModalController, NavController } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { of, Subject, Subscription } from "rxjs";
import { catchError, filter, map, shareReplay, switchMap, takeUntil, tap } from "rxjs/operators";
import { CommonVariable, LEAVE_SYNDICATE_FAILED } from "src/app/app.constants";
import { JackpotWarningComponent } from "src/app/components/jackpot-warning/jackpot-warning.component";
import { PausedSyndicateComponent } from "src/app/components/paused-syndicate-popup/paused-syndicate-popup.component";
import { SeeYouSoonComponent } from "src/app/components/see-you-soon/see-you-soon.component";
import { SyndicateRepositoryService } from "src/app/pages/syndicate/syndicate-repository.service";
import { SyndicateStatusComponent } from "src/app/pages/syndicate/syndicate-status/syndicate-status.component";
import { TakeHolidayComponent } from "src/app/pages/syndicate/take-holiday/take-holiday.component";
import { ChatbotWidgetService } from "src/app/services/chatbotwidget/chatbot-widget.service";
import { GTMSagmentService } from "src/app/services/gtm-sagment.service";
import { InProgressAnimationComponent } from "../../../components/in-progress-animation/in-progress-animation.component";
import { LeaveSyndicateComponent } from "../../syndicate/leave-syndicate/leave-syndicate.component";
import { UnpauseSyndicateBottomsheetComponent } from "../../syndicate/unpause-syndicate-bottomsheet/unpause-syndicate-bottomsheet.component";
import { AccountRepositoryService } from "../account-repository.service";

@Component({
  templateUrl: "./subscription-settings.component.html",
  styleUrls: ["./subscription-settings.component.scss"],
  standalone: true,
  imports: [
    IonicModule,
    NgStyle,
    NgClass,
    FormsModule,
    InProgressAnimationComponent,
    AsyncPipe,
    UpperCasePipe,
    DatePipe,
    TranslatePipe,
  ],
})
export class SubscriptionSettingsComponent implements OnInit, OnDestroy {
  private matDialog = inject(MatDialog);
  private router = inject(Router);
  private accountRepository = inject(AccountRepositoryService);
  public translate = inject(TranslateService);
  private authService = inject(AuthService);
  private GTMSegment = inject(GTMSagmentService);
  private modalCtrl = inject(ModalController);
  private syndicateService = inject(SyndicateRepositoryService);
  private navController = inject(NavController);
  private ChatbotWidgetService = inject(ChatbotWidgetService);

  showWidget: boolean = false;
  syndicateLoaded: boolean;
  jackpotLoaded: boolean;
  isHovered = false;
  setActive = true;
  setActiveTrigger = true;
  translatedToastMsg: any;
  selectedLanguage: string = "en";
  public widgetSubscription: Subscription;
  public preferredCurrency: string;
  public currencySymbol: string = CommonVariable.defaultCurrencySymbol;
  public spinnerAnimation: boolean;
  public progressBar: number;
  public sub: Data;
  public proceed = true;
  private hideTimeout: any;
  private declineSubscription: Subscription;
  private destroy$ = new Subject<void>();
  private dismissTimeout?: ReturnType<typeof setTimeout>;

  constructor() {
    console.log("the subscription settings component is loaded");
    this.preferredCurrency = CommonVariable.preferredCurrency;
    this.currencySymbol = CommonVariable.defaultCurrencySymbol;
    this.authService.selectedlanguage$.pipe(takeUntil(this.destroy$))
      ?.subscribe({
        next: (l) => {
          this.selectedLanguage = l;
          this.translate.use(l ?? 'en');
        },
        error: (err) => console.error(err)
      });

    this.authService.userDetails$
      ?.pipe(
        takeUntil(this.destroy$),
        tap(user => {
          this.preferredCurrency =
            user?.preferred_currency ?? CommonVariable.preferredCurrency;
        }),
        switchMap(() => this.authService.getCurrencyList$),
        filter(
          (data): data is { details: { currency_code: string; currency_symbol?: string }[] } =>
            !!data && Array.isArray((data as any).details)
        ),
        map(data =>
          data.details.find(x => x?.currency_code === this.preferredCurrency)
        )
      )
      .subscribe({
        next: currencyDetails => {
          this.currencySymbol =
            currencyDetails?.currency_symbol ?? CommonVariable.defaultCurrencySymbol;
        },
        error: err => console.error(err)
      });


  }


  myOngoingSyndicates$ = (this.accountRepository?.myOnGoingSyndicates$ || of([]))?.pipe(
    takeUntil(this.destroy$),
    map((res) => {
      console.log("myOngoingSyndicates$ emitted raw:", res);

      if (!res?.length) {
        console.warn("No ongoing syndicates found, returning null");
        return null; // Ensures safe handling of empty or null data
      }

      try {
        res?.forEach((syndicate) => {
          console.log("Processing syndicate:", syndicate);

          syndicate.active =
            syndicate?.current_status === ActiveStatus.Running ||
            syndicate?.current_status === ActiveStatus.Holiday;

          console.log(
            `Syndicate ${syndicate?.id || "unknown"} active status:`,
            syndicate.active
          );
        });

        console.log("Final mapped syndicates:", res);
        return res;
      } catch (e) {
        console.error("Error while processing syndicates:", e);
        return null;
      }
    }),
    tap(() => {
      this.syndicateLoaded = true;
      console.log("Syndicate data loaded, syndicateLoaded set to:", this.syndicateLoaded);
    }),
    catchError((error) => {
      console.error("Error in myOngoingSyndicates$ stream:", error);
      return of([]);
    })
  );


  myJackpots$ = (this.accountRepository?.myJackpots$ || of(null))?.pipe(takeUntil(this.destroy$),
    map((res: Data) => {
      if (!res) {
        this.syndicateLoaded = true;
        return null;
      }

      const activeJackpotArr: Data[] = [];
      const activeJackpots = res?.activeJackpots || [];
      const displayOrder = res?.jackpotTriggerOrder || [];
      displayOrder?.forEach((objOrder: Data) => {
        if (typeof objOrder !== 'string') return;
        const orderName: string = objOrder;
        const orderedActiveJackpots = activeJackpots?.find(
          (activeJackpot) =>
            typeof activeJackpot?.jackpot_name === 'string' &&
            activeJackpot?.jackpot_name?.toLowerCase() === orderName?.toLowerCase()
        );
        if (orderedActiveJackpots) activeJackpotArr.push(orderedActiveJackpots);
      });
      const orderedJackpots = activeJackpotArr?.length ? [...activeJackpotArr] : [];
      try {
        this.syndicateLoaded = true;
        const jc = orderedJackpots?.length ? orderedJackpots : activeJackpots;
        jc?.forEach((j) => (j.active = true));
        return jc?.length ? jc : null;
      } catch (e) {
        return null;
      }
    }),
    shareReplay(1),
    tap(() => {
      this.jackpotLoaded = true;
    })
  );
  openChatBot() {
    this.ChatbotWidgetService.openChatBot();
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
    this.ChatbotWidgetService.setWidgetVisibility(true);
    this.hideTimeout = setTimeout(() => {
      this.ChatbotWidgetService.setWidgetVisibility(false);
    }, 300000);
  }
  ngOnInit(): void {
    if (this.ChatbotWidgetService && this.ChatbotWidgetService.widgetVisibility$) {
      this.widgetSubscription = this.ChatbotWidgetService.widgetVisibility$?.pipe(takeUntil(this.destroy$)).subscribe(
        visible => {
          this.showWidget = visible;
          if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
          }
          if (visible) {
            this.hideTimeout = setTimeout(() => {
              this.ChatbotWidgetService.setWidgetVisibility(false);
            }, 600000);
          }
        }
      );
    }
  }

  formatCurrencyDisplay(amount: number | string | null | undefined): string {
    if (amount === null || amount === undefined || amount === "") {
      return "";
    }
    return formatDisplayCurrencyAmount(amount);
  }

  onSyndicateToggleChange(event, syndicate) {
    if (event.target.ariaChecked === "true") {
      syndicate.active = false;
    }
  }

  onTriggerToggleChange(event, trigger) {
    if (event.target.ariaChecked === "true") {
      trigger.active = false;
    }
  }


  async checkStatus(syndicateData) {
    try {
      if (syndicateData?.current_status === ActiveStatus.Running) {
        await this.ongoingWarning(syndicateData);
      } else if (syndicateData?.current_status === ActiveStatus.Holiday) {
        this.pausedSyndicatePopup(syndicateData);
      } else if (syndicateData?.current_status === ActiveStatus.Paused) {
        this.unpauseSyndicate(syndicateData);
      }
    } catch (e) {
      this.authService.handleErrorResponse(e);
    }
  }

  unpauseSyndicate(syndicate) {
    const pauseSyndicateDialog = this.matDialog.open(UnpauseSyndicateBottomsheetComponent, {
      panelClass: "unpause-syndicate-popup",
      data: { syndicate: syndicate },
    });

    pauseSyndicateDialog?.afterClosed()?.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data) {
        this.modalCtrl.dismiss({ data: "close" });
      } else {
        syndicate.active = false;
      }
    });
  }

  async ongoingWarning(syndicateData) {
    try {
      syndicateData.fromSubscription = true;
      /* open syndicate status from the syndicate settings */
      this.GTMSegment.HotJarEvent('user_manage_syndicate');
      const syndicateSettings = await this.modalCtrl.create({
        component: SyndicateStatusComponent,
        cssClass: "modal-fullScreen",
        componentProps: {
          syndicateData,
        },
      });

      /* when modal syndicate modal dismissed */
      syndicateSettings.onDidDismiss().then(async (res: Data) => {
        try {
          if (res?.data?.data === "close") {
            syndicateData.active = true;
            this.dismissTimeout = setTimeout(() => {
              this.modalCtrl.dismiss().catch((err) => {
                return err;
              });
            }, 300);
            return;
          } else if (res?.data?.data === "syndicatePaused") {
            this.dismissTimeout = setTimeout(() => {
              this.modalCtrl.dismiss().catch((err) => {
                return err;
              });
            }, 300);
          } else if (res?.data?.data === "leaveReason") {
            return;
          } else if (res?.data?.data === "keepPlaying") {
            syndicateData.active = true;
            this.modalCtrl.dismiss().catch((err) => {
              return err;
            });
            return;
          } else if (res?.data) {
            await this.leaveSyndicateNextStep(res.data, syndicateData);
          } else {
            syndicateData.active = true
          }
        } catch (e) {
          this.authService.handleErrorResponse(e);
          syndicateData.active = true;
        }
      }).catch((error) => {
        this.authService.handleErrorResponse(error);
        syndicateData.active = true;
      });

      return await syndicateSettings.present();
    } catch (e) {
      return e;
    }
  }

  openPrefModal() {
    this.router.navigateByUrl('/close-your-account');
  }

  pausedSyndicatePopup(syndicateData) {
    try {
      const pausedSyndicatePopup = this.matDialog.open(PausedSyndicateComponent, {
        panelClass: "jackpotWarning-size",
        backdropClass: "jackpotWarning-back-drop-color",
        data: { syndicate: syndicateData, reason: "" },
      });

      pausedSyndicatePopup.afterClosed()?.pipe(takeUntil(this.destroy$)).subscribe(async (modalData) => {
        try {
          if (modalData?.leaveClicked) {
            const leaveSyndicateReason = await this.modalCtrl.create({
              component: LeaveSyndicateComponent,
              cssClass: "modal-fullScreen",
              componentProps: { syndicate: syndicateData },
            });
            leaveSyndicateReason.onDidDismiss().then(async (res) => {
              if (res?.data) {
                setTimeout(async () => {
                  try {
                    await this.leaveSyndicateNextStep(res.data, syndicateData);
                  } catch (e) {
                    this.authService.handleErrorResponse(e);
                    syndicateData.active = true;
                  }
                }, 100);
              } else {
                syndicateData.active = true;
              }
            }).catch((error) => {
              this.authService.handleErrorResponse(error);
              syndicateData.active = true;
            });
            return await leaveSyndicateReason.present();
          } else {
            syndicateData.active = true;
          }
        } catch (e) {
          this.authService.handleErrorResponse(e);
          syndicateData.active = true;
        }
      });
    } catch (err) {
      return err
    }
  }

  private async leaveSyndicateNextStep(reason, syndicateData) {
    const syndicateHoliday = await this.modalCtrl.create({
      component: TakeHolidayComponent,
      cssClass: "modal-fullScreen",
      componentProps: {
        syndicateData,
        reason,
      },
    });
    syndicateHoliday.onDidDismiss().then((res: Data) => {
      if (res?.data?.leaveFinal) {
        this.leaveSyndicate(reason, syndicateData);
        //this.leaveSyndicateConfirmation(reason, syndicateData);
      } else if (!res?.data?.leaveFinal) {
        this.dismissTimeout = setTimeout(() => {
          this.modalCtrl.dismiss().catch((err) => {
            return err;
          });
        }, 300);
      } else {
        syndicateData.active = true;
        return;
      }
    }).catch((error) => {
      return error;
    });

    return await syndicateHoliday.present();
  }

  public leaveSyndicate(reason, syndicateData) {
    try {
      this.syndicateService
        .leaveSyndicate(
          syndicateData?.id
            ? syndicateData?.id
            : syndicateData?.encrypted_id
              ? syndicateData?.encrypted_id
              : syndicateData?.sId,
          syndicateData?.cardType,
          reason
        ).pipe(takeUntil(this.destroy$))
        ?.subscribe((response) => {
          this.progressBar === 100;
          this.sub?.unsubscribe();
          if (response?.status === "rejected") {
            this.authService.getConstantsMsg(response?.error_code).then((translatedMsg) => {
              const defaultMsg = {
                message: {
                  line_1: "Something went wrong!",
                  line_2: "",
                },
                style: {
                  icon: "tick circle error.svg",
                  type: "Error",
                },
              }
              this.authService.showToastMessage(translatedMsg, defaultMsg);
            }).catch((error) => {
              return error;
            });
            this.modalCtrl.dismiss().catch((err) => {
              return err;
            });
            if (syndicateData?.fromSubscription) {
              this.router.navigateByUrl("mainTabs/tabs/syndicate");
            }
            return;
          }
          const res = "success";
          if (res === "success") {
            const msg = this.matDialog.open(SeeYouSoonComponent, {
              height: "50%",
              data: syndicateData,
            });

            msg?.afterClosed()?.pipe(takeUntil(this.destroy$)).subscribe(
              () => {
                this.modalCtrl.dismiss({ isSuccessLeave: true }).catch((err) => {
                  return err;
                });
                if (syndicateData?.fromSubscription) {
                  this.router.navigateByUrl("mainTabs/tabs/syndicate");
                }
              },
              (err) => {
                return err;
              }
            );
          } else {
            this.dismissTimeout = setTimeout(() => {
              const translatedMsg = {
                message: {
                  line_1: LEAVE_SYNDICATE_FAILED,
                  line_2: "",
                },
                style: {
                  duration: 2000,
                  icon: "tick circle error.svg",
                  type: "Error",
                }
              }
              const defaultMsg = {
                message: {
                  line_1: "Leaving syndicate failed",
                  line_2: "",
                },
                style: {
                  icon: "tick circle error.svg",
                  type: "Error",
                },
              }
              this.authService.showToastMessage(translatedMsg, defaultMsg);
            }, 2000);
            this.modalCtrl.dismiss().catch((err) => {
              return err;
            });
            if (syndicateData?.fromSubscription) {
              this.router.navigateByUrl("mainTabs/tabs/syndicate");
            }
          }

          this.dismissTimeout = setTimeout(() => {
            this.spinnerAnimation = false;
            this.proceed = false;
            setTimeout(() => {
              this.proceed = true;
            }, 2000);
          }, 300);
        });
    } catch (e) {
      return e;
    }
  }

  jackpotWarning(trigger) {
    const decline = this.matDialog.open(JackpotWarningComponent, {
      panelClass: "jackpotWarning-size",
      backdropClass: "jackpotWarning-back-drop-color",
      data: { trigger },
    });

    this.declineSubscription = decline.afterClosed()?.pipe(takeUntil(this.destroy$)).subscribe((d) => {
      // Only treat confirmed backend deactivation as success.
      // Cancel / dismiss / failed deactivate must restore the toggle.
      // Analytics failures must not revert subscription UI state.
      if (!d) {
        trigger.active = true;
        return;
      }

      trigger.active = false;

      try {
        const expiryDate = trigger?.expiry_date?.seconds
          ? trigger.expiry_date.seconds * 1000
          : null;
        const now = new Date().getTime();
        const syndicateDuration = expiryDate
          ? this.formatTime(expiryDate - now)
          : null;
        const body = {
          Syndicate_Name: trigger?.jackpot_name,
          Prosub_ID: trigger?.offer_link_id ?? trigger?.prosub_id ?? trigger?.id,
          Offer_ID: trigger?.offer_id,
          Syndicate_Duration: syndicateDuration,
        };
        this.GTMSegment?.GTMEventPopupInitialization(
          "Subscription Deactivated Via Account",
          body
        );
      } catch (error) {
        this.authService?.errorHandlerLogs?.(
          "[SubscriptionSettings] GTM after deactivate failed",
          error
        );
      }
    });

  }

  navigateToAccountTab() {
    this.router.navigateByUrl("account");
  }

  goToStore() {
    this.router.navigateByUrl("mainTabs/tabs/store");
  }

  transform(value) {
    if (!value) return "N/A";
    value = value.trim();
    try {
      if (!value || value?.length > 3) {
        return value;
      }
    } catch (e) {
      return e;
    }
    switch (value) {
      case "Mon":
        return "Monday";

      case "Tue":
        return "Tuesday";

      case "Wed":
        return "Wednesday";

      case "Thu":
        return "Thursday";

      case "Fri":
        return "Friday";

      case "Sat":
        return "Saturday";

      case "Sun":
        return "Sunday";

      default:
        return "Not Selected";
    }
  }

  goBack() {
    this.navController.back();
  }
  //Formats milliseconds into a human-readable time string.
  private formatTime(ms: number): string {
    const seconds = Math.floor((ms / 1000) % 60);
    const minutes = Math.floor((ms / (1000 * 60)) % 60);
    const hours = Math.floor((ms / (1000 * 60 * 60)) % 24);
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));

    if (days > 0) {
      return `${days}d ${hours}hrs ${minutes}m`;
    } else {
      let formattedTime = "";

      if (hours > 0) {
        formattedTime += `${hours}hrs `;
      }
      if (minutes > 0) {
        formattedTime += `${minutes}m `;
      }
      if (seconds > 0) {
        formattedTime += `${seconds}s`;
      }

      return formattedTime.trim();
    }
  }
  /**
 * Toggles the isHovered state based on the provided boolean value.
 *
 * @param state - A boolean value representing the new hover state.
 *                If true, sets isHovered to true, indicating the element is being hovered over.
 *                If false, sets isHovered to false, indicating the element is no longer being hovered over.
 */
  toggleZIndex(state: boolean): void {
    this.isHovered = state;
  }

  ngOnDestroy() {
    // Emit a value to complete all subscriptions using takeUntil(this.destroy$)
    this.destroy$.next();
    this.destroy$.complete();
    // Clear chatbot auto-hide timeout if it exists
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
    // Clear modal dismissal timeout (e.g., for syndicate modals) if it exists
    if (this.dismissTimeout) {
      clearTimeout(this.dismissTimeout);
    }
    // Unsubscribe from declineSubscription to prevent memory leaks
    if (this.declineSubscription) {
      this.declineSubscription.unsubscribe();
    }
  }

}
